/**
 * Client accessors for the per-venue deposit/prepayment reconciliation surface (backend
 * `DepositReconciliationController`, deposits-and-prepayment.md, R6/R7/R9/R10). Every call funnels through
 * `@repo/api-client`'s fetch mutator (bearer token, RFC 9457 `ApiError`). Shapes are declared locally until
 * the OpenAPI-generated models land (`just gen`); field names mirror the controller DTOs exactly. The report
 * + refund are MANAGER-gated server-side (a WAITER gets 403).
 */
import { customInstance } from "@repo/api-client";

export type Money = { amountMinor: number; currency: string };

export type ReconciliationRow = {
  paymentRef: string;
  bookingRef: string;
  primitive: "DEPOSIT" | "PREPAYMENT";
  status:
    | "AWAITING_PAYMENT"
    | "PAID"
    | "PARTIALLY_REFUNDED"
    | "REFUNDED"
    | "PARTIALLY_FORFEITED"
    | "FORFEITED"
    | "REFUND_FAILED";
  collected: Money;
  collectedTax: Money;
  refunded: Money;
  refundedTax: Money;
  forfeited: Money;
  forfeitedTax: Money;
  feesAtCost: Money;
  forfeitureTaxable: boolean;
  occurredAtSec: number;
};

export type ReconciliationTotals = {
  collectedMinor: number;
  collectedTaxMinor: number;
  refundedMinor: number;
  refundedTaxMinor: number;
  forfeitedMinor: number;
  forfeitedTaxMinor: number;
  feesAtCostMinor: number;
};

export type Reconciliation = {
  fromSec: number;
  toSec: number;
  currency: string;
  rows: ReconciliationRow[];
  totals: ReconciliationTotals;
};

export type RefundResult = {
  status: string;
  movementRef: string;
  refundedMinor: number;
  refundedTaxMinor: number;
};

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/deposits`;

export const depositsApi = {
  reconcile: (venueId: string, from: string, to: string, signal?: AbortSignal) =>
    customInstance<Reconciliation>(`${base(venueId)}/reconciliation?from=${from}&to=${to}`, {
      method: "GET",
      signal,
    }),

  refund: (
    venueId: string,
    bookingRef: string,
    amountMinor: number,
    currency: string,
    reason: string,
  ) =>
    customInstance<RefundResult>(`${base(venueId)}/${encodeURIComponent(bookingRef)}/refund`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amountMinor, currency, reason }),
    }),
};

/** Format integer minor units as a localized currency amount (ADR-0024 — never a float in transit). */
export function formatMoney(m: Money, locale = "fr-FR"): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: m.currency }).format(
    m.amountMinor / 100,
  );
}
